import React, { useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MessageSquare, Sparkles, FileText } from 'lucide-react';
import ContentGenerationForm from '@/components/ai-content/ContentGenerationForm';
import GeneratedContentDisplay from '@/components/ai-content/GeneratedContentDisplay';
import GenerationHistory from '@/components/ai-content/GenerationHistory';
import { motion } from 'framer-motion';
import { Separator } from '@/components/ui/separator';
import { StandardPageHeader } from '@/components/layout/StandardPageHeader';

// Animation variants
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
const AIContentGenerator = () => {
  const [prompt, setPrompt] = useState('');
  const [generatedContent, setGeneratedContent] = useState('');
  const [contentType, setContentType] = useState('announcement');
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('create');
  const [contentTitle, setContentTitle] = useState('');
  const [recentGenerations, setRecentGenerations] = useState<any[]>([]);
  const contentTypeLabels = {
    announcement: 'Announcement',
    marketing: 'Marketing Copy',
    notification: 'Notification'
  };
  const generateContent = async () => {
    if (!prompt) {
      toast.error('Please enter a prompt');
      return;
    }
    setIsGenerating(true);
    setGeneratedContent('');
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke('generate-ai-content', {
        body: {
          prompt,
          type: contentType
        }
      });
      if (error) throw error;
      setGeneratedContent(data.content);
      toast.success('Content generated successfully');

      // Update the recent generations list
      if (data.notification_id) {
        const newGeneration = {
          id: data.notification_id,
          content: data.content,
          type: contentType,
          created_at: new Date().toISOString()
        };
        setRecentGenerations(prev => [newGeneration, ...prev]);
      }
    } catch (error) {
      console.error('Error generating content:', error);
      toast.error('Failed to generate content');
    } finally {
      setIsGenerating(false);
    }
  };
  const saveAsNotification = async () => {
    if (!generatedContent) {
      toast.error('Please generate content first');
      return;
    }
    try {
      const title = contentTitle || `${contentTypeLabels[contentType as keyof typeof contentTypeLabels]} - ${new Date().toLocaleDateString()}`;

      // The content is already saved in the notifications table by the edge function
      toast.success('Content saved as notification');

      // Reset form
      setPrompt('');
      setGeneratedContent('');
      setContentTitle('');
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Failed to save content');
    }
  };
  return <motion.div className="space-y-6 p-6" variants={containerVariants} initial="hidden" animate="visible">
      <motion.div variants={itemVariants}>
        <StandardPageHeader
          title="AI Content Generator"
          subtitle="Generate professional content for your hive"
          icon={Sparkles}
        />
      </motion.div>

      <motion.div variants={itemVariants}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full max-w-md grid-cols-2 bg-white/80 backdrop-blur-sm">
            <TabsTrigger value="create" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              <span>Create Content</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              <span>Recent Generations</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="create" className="space-y-4">
            <div className="bg-white/50 backdrop-blur-sm rounded-xl border border-gray-100/60 shadow-sm">
              <ContentGenerationForm prompt={prompt} setPrompt={setPrompt} contentType={contentType} setContentType={setContentType} isGenerating={isGenerating} onGenerate={generateContent} />
            </div>

            {(isGenerating || generatedContent) && <div className="bg-white/50 backdrop-blur-sm rounded-xl border border-gray-100/60 shadow-sm">
                <GeneratedContentDisplay isGenerating={isGenerating} generatedContent={generatedContent} contentTitle={contentTitle} setContentTitle={setContentTitle} onSaveAsNotification={saveAsNotification} />
              </div>}
          </TabsContent>

          <TabsContent value="history">
            <div className="bg-white/50 backdrop-blur-sm rounded-xl border border-gray-100/60 shadow-sm">
              <GenerationHistory generations={recentGenerations} contentTypeLabels={contentTypeLabels} onCreateNew={() => setActiveTab('create')} />
            </div>
          </TabsContent>
        </Tabs>
      </motion.div>
    </motion.div>;
};
export default AIContentGenerator;
//comment